import type { FormFragment } from '@seek/cmsu-cms-connect';
import { createForm } from '@seek/forms-ui';
import type { Country, SiteName } from '@seek/melways-sites';
import { VocabProvider } from '@vocab/react';
import { Alert, ContentBlock, Text } from 'braid-design-system';

import { useCMSUContext } from '../../hooks/useCMSUContext';
import type { ButtonStyleProps } from '../types';

import { Form } from './Form';
import { createFieldActualKey, createFormValues } from './formSettingHelper';
import type {
  AutofillableValue,
  DynamicFormValues,
  FormWithActualKey,
} from './types';

export const CForm = ({
  locale,
  sourceName,
  privacyUrl,
  recaptchaKey,
  country,
  site,
  wptSalesForceApi,
  form,
  sectionName,
  autofillableValue,
}: {
  locale: string;
  sourceName: string;
  privacyUrl: string;
  recaptchaKey: string;
  country: Country;
  site: SiteName;
  wptSalesForceApi: string;
  submitBtnStyle?: ButtonStyleProps;
  form: FormFragment;
  sectionName?: string;
  autofillableValue: AutofillableValue;
}) => {
  const { FormProvider, useField } = createForm<DynamicFormValues>();
  const { language } = useCMSUContext();
  if (!form || !form.fields) {
    return (
      <ContentBlock>
        <Alert tone="caution">
          <Text>
            An error occured loading the form fields. The requested C_Form might
            not be published or the form fields are not available.
          </Text>
        </Alert>
      </ContentBlock>
    );
  }

  const formValues: DynamicFormValues = createFormValues(
    form && form.fields ? form.fields : [],
    autofillableValue,
  );
  const initialValues: DynamicFormValues | null = formValues;
  const formWithActualKey: FormWithActualKey = {
    ...form,
    fields: createFieldActualKey(form) as FormWithActualKey['fields'],
  };
  return (
    <ContentBlock>
      <VocabProvider language={language}>
        {form ? (
          <FormProvider initialValues={initialValues}>
            {(formProps) => (
              <Form
                {...formProps}
                locale={locale}
                useField={useField}
                recaptchaKey={recaptchaKey}
                country={country}
                site={site}
                sourceName={sourceName}
                privacyUrl={privacyUrl}
                wptSalesForceApi={wptSalesForceApi}
                form={formWithActualKey}
                sectionName={sectionName}
              />
            )}
          </FormProvider>
        ) : (
          <Alert tone="caution">
            <Text>
              If you&apos;re seeing this, the form might not be published
            </Text>
          </Alert>
        )}
      </VocabProvider>
    </ContentBlock>
  );
};
